import React, { useState, useRef, useEffect } from "react";
import {
  FaFileMedicalAlt,
  FaTimes,
  FaSave,
  FaRecycle,
} from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import api from "../../config/configApi";
import Logo from "../../images/logopdf.png";
import WatermarkLogo from "../../assets/afrafep.png";
import { jsPDF } from "jspdf";

interface Professional {
  nmProfissional: string;
  nomePaciente: string;
  crm: string;
  cpf: string;
  idProfissional: string;
}

interface ReceituarioLaudo {
  id: number;
  tipo: string;
  dtConsulta: string;
  hrConsulta: string;
  cid: string;
  atestadoQtdDias: number;
  prescricao: string;
  criadoEm: string;
  editadoEm: string;
  profissional: {
    idProfissional: number;
    nmProfissional: string;
    CRM?: string;
    especialidade: {
      idEspecialidade?: number;
      dsEspecialidade: string;
      exibeQuestEpidem?: string;
      realizaProced?: string;
    };
  };
  paciente: {
    cdPaciente: number;
    nrCarteiraPlano: string;
    nmPaciente: string;
    dtNascimento: string;
    flSexo: string;
    cdStatus: number;
    nuCpf: string;
    nmEndereco: string;
    nuEndereco: number;
    nmComplEndereco: string;
    nmBairroEndereco: string;
    nmCidadeEndereco: string;
    nmUfEndereco: string;
    cdCepEndereco: string;
    cartaoSus: string;
  };
}

const ReceituarioControleEspecial: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [content, setContent] = useState("");
  const [, setShowPreview] = useState(false);
  const [receituariosExistentes, setReceituariosExistentes] = useState<
    ReceituarioLaudo[]
  >([]);
  const [showReceituariosList, setShowReceituariosList] = useState(false);
  const [loadingReceituarios, setLoadingReceituarios] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const dataAtual = new Date().toLocaleDateString("pt-BR");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const enderecoParts = [
    Cookies.get("nmEndereco"),
    Cookies.get("nuEndereco"),
    Cookies.get("nmComplEndereco"),
    Cookies.get("nmBairroEndereco"),
    Cookies.get("nmCidadeEndereco"),
    Cookies.get("nmUfEndereco"),
  ]
    .filter((v) => v && v.trim().toLowerCase() !== "null")
    .join(", ");

  const enderecoPaciente = enderecoParts;

  const professional: Professional = {
    nmProfissional: Cookies.get("nmProfissional") || "",
    nomePaciente: Cookies.get("Nome") || "",
    crm: Cookies.get("CRM") || "",
    cpf: Cookies.get("NuCPF") || "",
    idProfissional: Cookies.get("idProfissional") || "",
  };

  // Função para buscar receituários especiais existentes do paciente
  const fetchReceituariosExistentes = async () => {
    setLoadingReceituarios(true);
    try {
      const token = sessionStorage.getItem("token");
      const cpf = professional.cpf;

      if (token && cpf) {
        const response = await api.get<ReceituarioLaudo[]>(
          `/sisclinic/receituarios/cpf/${cpf}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // Filtrar apenas receituários especiais (tipo "RECEITA_ESPECIAL") e ordenar por data mais recente
        const receituarios = response.data
          .filter((item) => item.tipo === "RECEITA_ESPECIAL")
          .sort((a, b) => {
            const dateA = new Date(`${a.dtConsulta}T${a.hrConsulta}`);
            const dateB = new Date(`${b.dtConsulta}T${b.hrConsulta}`);
            return dateB.getTime() - dateA.getTime();
          });

        setReceituariosExistentes(receituarios);
      }
    } catch (error) {
      console.error("Erro ao buscar receituários especiais existentes:", error);
      toast.error("Erro ao carregar receituários anteriores");
    } finally {
      setLoadingReceituarios(false);
    }
  };

  // Função para carregar um receituário existente no editor
  const carregarReceituarioExistente = (receituario: ReceituarioLaudo) => {
    setContent(receituario.prescricao);
    setShowReceituariosList(false);
    toast.success("Receituário carregado com sucesso!");
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["clean"],
    ],
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isModalOpen) setIsModalOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isModalOpen]);

  const handleClose = () => {
    setIsModalOpen(false);
    setShowPreview(false);
    setShowReceituariosList(false);
  };

  // Função para formatar data CORRIGIDA - sem problemas de fuso horário
  const formatDate = (dateString: string) => {
    if (!dateString) return "";

    // Divide a data no formato YYYY-MM-DD
    const [year, month, day] = dateString.split("-");

    // Cria a data no formato DD/MM/YYYY sem problemas de fuso horário
    return `${day}/${month}/${year}`;
  };

  // Função melhorada para converter HTML em texto formatado preservando quebras de linha
  const htmlToFormattedText = (html: string): string => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // Função recursiva para processar nós
    const processNode = (node: Node, isList: boolean = false): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent?.replace(/\s+/g, " ") || "";
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const tagName = element.tagName.toLowerCase();
        switch (tagName) {
          case "p":
            const pContent = Array.from(element.childNodes)
              .map((child) => processNode(child))
              .join("");
            return pContent + "\n"; // Apenas uma quebra de linha para parágrafos

          case "br":
            return "\n";

          case "div":
            return (
              Array.from(element.childNodes)
                .map((child) => processNode(child))
                .join("") + "\n"
            );

          case "strong":
          case "b":
            return Array.from(element.childNodes)
              .map((child) => processNode(child))
              .join("");

          case "em":
          case "i":
            return Array.from(element.childNodes)
              .map((child) => processNode(child))
              .join("");

          case "ul":
            return Array.from(element.children)
              .map((li) => "• " + processNode(li, true).trim() + "\n")
              .join("");

          case "ol":
            return Array.from(element.children)
              .map(
                (li, index) =>
                  index + 1 + ". " + processNode(li, true).trim() + "\n"
              )
              .join("");

          case "li":
            const liContent = Array.from(element.childNodes)
              .map((child) => processNode(child, true))
              .join("")
              .trim();
            return isList ? liContent : "• " + liContent + "\n";

          default:
            return Array.from(element.childNodes)
              .map((child) => processNode(child, isList))
              .join("");
        }
      }
      return "";
    };

    let result = Array.from(tempDiv.childNodes)
      .map((node) => processNode(node))
      .join("");

    // Limpar formatação excessiva
    result = result
      .replace(/\n\s*\n/g, "\n") // Apenas uma quebra de linha entre blocos
      .replace(/[ \t]+/g, " ") // Remover espaços múltiplos
      .replace(/\n /g, "\n") // Remover espaços no início de linhas
      .replace(/ \n/g, "\n") // Remover espaços no final de linhas
      .trim();

    return result;
  };
  // Função para obter texto de pré-visualização
  const obterTextoPreview = (): string => {
    return htmlToFormattedText(content) || "(Nenhum conteúdo digitado)";
  };

  const handleGeneratePDF = async () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const halfWidth = pageWidth / 2;
    const margin = 40;
    const footerHeight = 120;

    // Função para adicionar marca d'água
    const addWatermark = async (x: number, y: number, width: number) => {
      const watermarkImg = new Image();
      watermarkImg.src = WatermarkLogo;

      return new Promise((resolve) => {
        watermarkImg.onload = () => {
          const watermarkHeight =
            (watermarkImg.height * width) / watermarkImg.width;

          const canvas = document.createElement("canvas");
          canvas.width = watermarkImg.width;
          canvas.height = watermarkImg.height;
          const ctx = canvas.getContext("2d");
          if (!ctx) return;

          ctx.drawImage(watermarkImg, 0, 0, canvas.width, canvas.height);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;

          for (let i = 0; i < data.length; i += 4) {
            if (data[i] > 150 && data[i + 1] < 80 && data[i + 2] < 80) {
              data[i] = 36;
              data[i + 1] = 99;
              data[i + 2] = 235;
              data[i + 3] = 30;
            }
          }

          ctx.putImageData(imageData, 0, 0);

          doc.addImage(
            canvas,
            "PNG",
            x + (halfWidth - width) / 2,
            y + (pageHeight - watermarkHeight) / 2,
            width,
            watermarkHeight,
            undefined,
            "FAST"
          );
          resolve(null);
        };
      });
    };

    // Cabeçalho com logo para cada via
    const addHeader = async (x: number, y: number): Promise<number> => {
      const logoImg = new Image();
      logoImg.src = Logo;

      return new Promise((resolve) => {
        logoImg.onload = () => {
          const logoWidth = halfWidth - margin * 2;
          const logoHeight = (logoImg.height * logoWidth) / logoImg.width;

          doc.addImage(logoImg, "PNG", x + margin, y, logoWidth, logoHeight);

          // Texto com ambas as vias
          doc.setFontSize(9);
          doc.setTextColor(0, 0, 0);

          // Primeira via
          doc.text(
            "1ª Via - Retenção da Farmácia ou Drogaria",
            x + halfWidth - margin,
            y + logoHeight + 12,
            { align: "right" }
          );

          // Segunda via
          doc.text(
            "2ª Via - Orientação ao Paciente",
            x + halfWidth - margin,
            y + logoHeight + 24,
            { align: "right" }
          );

          resolve(logoHeight);
        };
      });
    };

    // Função para adicionar rodapé
    const addFooter = (x: number, y: number) => {
      doc.setFontSize(6);
      doc.setTextColor(100, 100, 100);

      // Esquerda
      const leftColX = x + margin;
      const startY = y + 20;

      doc.text(
        "Rua Corálio Soares de Oliveira, 497 • Centro, João Pessoa • PB",
        leftColX,
        startY
      );
      doc.text("Fone: (83) 3533.5310 | 3533.5311", leftColX, startY + 10);
      doc.text("www.afrafep.org.br", leftColX, startY + 20);
      doc.text("INSC. ANS N° 330281", leftColX, startY + 30);

      // Direita - Valores
      doc.setTextColor(0, 0, 200);
      const rightColX = x + halfWidth - margin - 120;
      doc.setFontSize(6);
      doc.text("Ética e Transparência", rightColX, startY);
      doc.text(
        "Solidariedade e Responsabilidade Social",
        rightColX,
        startY + 10
      );
      doc.text("Comprometimento e Profissionalismo", rightColX, startY + 20);
      doc.text("Legalidade e Respeito", rightColX, startY + 30);
    };

    const drawContent = async (x: number, y: number) => {
      let currentY = y;

      // Marca d'água
      await addWatermark(x, currentY, 180);

      // Cabeçalho
      const logoHeight = await addHeader(x, currentY);
      currentY += logoHeight + 40;

      // --- TÍTULO RECEITUÁRIO ---
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("RECEITUÁRIO CONTROLE ESPECIAL", x + halfWidth / 2, currentY, {
        align: "center",
      });
      currentY += 25;

      // 1. Identificação do Emitente
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("IDENTIFICAÇÃO DO EMITENTE", x + margin, currentY);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);

      doc.text(
        `Nome completo: Dr. ${professional.nmProfissional}`,
        x + margin,
        currentY + 18
      );
      doc.text("CNPJ: 09.306.242/0001-82", x + margin, currentY + 32);

      const enderecoEmitenteText = doc.splitTextToSize(
        "Endereço completo e Telefone: Rua Corálio Soares de Oliveira – 497, Centro (83) 3533.5310",
        halfWidth - margin * 2 - 15
      );
      doc.text(enderecoEmitenteText, x + margin, currentY + 46);

      const enderecoEmitenteHeight = enderecoEmitenteText.length * 9;

      doc.text(
        "Cidade: João Pessoa",
        x + margin,
        currentY + 46 + enderecoEmitenteHeight + 8
      );
      doc.text(
        "UF: Paraíba",
        x + margin,
        currentY + 46 + enderecoEmitenteHeight + 20
      );

      currentY += 46 + enderecoEmitenteHeight + 35;

      // Linha divisória
      doc.setDrawColor(200, 200, 200);
      doc.line(x + margin, currentY, x + halfWidth - margin, currentY);
      currentY += 12;

      // 2. Informações do Paciente
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("INFORMAÇÕES DO PACIENTE", x + margin, currentY);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);

      const pacienteText = doc.splitTextToSize(
        `Paciente: ${professional.nomePaciente || "Não informado"}`,
        halfWidth - margin * 2
      );
      doc.text(pacienteText, x + margin, currentY + 18);

      const enderecoPacienteText = doc.splitTextToSize(
        `Endereço: ${enderecoPaciente || "Não informado"}`,
        halfWidth - margin * 2
      );
      doc.text(enderecoPacienteText, x + margin, currentY + 32);

      const enderecoPacienteHeight = enderecoPacienteText.length * 9;
      currentY += 32 + enderecoPacienteHeight + 8;

      // Linha divisória
      doc.line(x + margin, currentY, x + halfWidth - margin, currentY);
      currentY += 12;

      // 3. Prescrição
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("PRESCRIÇÃO MÉDICA", x + margin, currentY);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);

      const prescricaoText = htmlToFormattedText(content);
      const splitPrescricao = doc.splitTextToSize(
        prescricaoText,
        halfWidth - margin * 2
      );

      currentY += 18;
      for (let i = 0; i < splitPrescricao.length; i++) {
        if (currentY > pageHeight - footerHeight - 20) {
          addFooter(x, pageHeight - footerHeight);
          doc.addPage();
          currentY = margin;
          await addWatermark(x, currentY, 180);
          const newLogoHeight = await addHeader(x, currentY);
          currentY += newLogoHeight + 35;
        }

        doc.text(splitPrescricao[i], x + margin, currentY);
        currentY += 11;
      }

      currentY += 18;

      // 4. Assinatura do Médico + CRM
      const signatureLineLength = (halfWidth - margin * 2) * 0.6;
      const signatureLineX = x + (halfWidth - signatureLineLength) / 2;

      doc.setDrawColor(0, 0, 0);
      doc.line(
        signatureLineX,
        currentY,
        signatureLineX + signatureLineLength,
        currentY
      );

      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.text(
        "CARIMBO E ASSINATURA DO MÉDICO",
        x + halfWidth / 2,
        currentY + 12,
        { align: "center" }
      );

      // --- CRM do Médico ---
      doc.setFont("helvetica", "normal");
      doc.text(
        `CRM: ${professional.crm || "Não informado"}`,
        x + halfWidth / 2,
        currentY + 24,
        { align: "center" }
      );

      // 5. Data
      doc.setFont("helvetica", "normal");
      doc.text(`Data: ${dataAtual}`, x + margin, currentY + 40);
      currentY += 55;

      // 6. Identificação do Comprador/Fornecedor
      const colWidth = (halfWidth - margin * 2 - 20) / 2;

      // Comprador
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text(
        "IDENTIFICAÇÃO DO COMPRADOR",
        x + margin + colWidth / 2,
        currentY,
        { align: "center" }
      );

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text("Nome: ______________________", x + margin, currentY + 18);
      doc.text(
        "Ident.: ____________ Orgão Emissor: ______",
        x + margin,
        currentY + 32
      );
      doc.text("End.: _________________________", x + margin, currentY + 46);
      doc.text(
        "Cidade: _____________________ UF: _____",
        x + margin,
        currentY + 60
      );
      doc.text("Telefone: ____________", x + margin, currentY + 74);

      // Linha separadora entre Comprador e Fornecedor
      doc.setDrawColor(200, 200, 200);
      doc.line(
        x + margin + colWidth + 10,
        currentY,
        x + margin + colWidth + 10,
        currentY + 85
      );

      // Fornecedor
      const rightColX = x + margin + colWidth + 20;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text(
        "IDENTIFICAÇÃO DO FORNECEDOR",
        rightColX + colWidth / 2,
        currentY,
        { align: "center" }
      );

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text("Assinatura do Farmacêutico:", rightColX, currentY + 18);
      doc.setDrawColor(0, 0, 0);
      doc.line(rightColX, currentY + 22, rightColX + colWidth, currentY + 22);
      doc.text("Data: ____/____/_____", rightColX, currentY + 40);

      // Rodapé
      addFooter(x, pageHeight - footerHeight + 35);
    };

    // Desenhar conteúdo em ambas as vias
    await drawContent(0, 0);
    await drawContent(halfWidth, 0);

    // Linha divisória central
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(1);
    doc.line(halfWidth, 0, halfWidth, pageHeight);

    // Gerar PDF e abrir impressão
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = pdfUrl;
    document.body.appendChild(iframe);

    iframe.onload = function () {
      setTimeout(function () {
        iframe.contentWindow?.print();
      }, 500);
    };

    return pdfBlob;
  };

  const handleSaveReceituario = async () => {
    if (!content || content === "<p><br></p>") {
      toast.error("O campo prescrição do receituário especial é obrigatório!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    setIsSubmitting(true); // DESABILITA o botão

    const nuCpfCookie = Cookies.get("NuCPF") || "";
    const nuCpfSomenteNumeros = nuCpfCookie.replace(/\D/g, "");
    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().split("T")[0];
    const formattedTime = currentDate.toTimeString().substring(0, 5);

    const payload = {
      nuCpf: nuCpfSomenteNumeros,
      idProfissional: Number(professional.idProfissional) || 0,
      tipo: "RECEITA_ESPECIAL",
      dtConsulta: formattedDate,
      hrConsulta: formattedTime,
      prescricao: content,
    };

    try {
      const response = await api.post("/receituarios", payload);

      if (response.status === 200 || response.status === 201) {
        toast.success("Receituário Controle Especial salvo com sucesso!");
        setTimeout(() => {
          handleGeneratePDF();
          setTimeout(() => {
            handleClose();
            setContent("");
            setIsSubmitting(false); // REABILITA o botão ao fechar
          }, 3500);
        }, 1000);
      } else {
        throw new Error(
          `Erro ao salvar receituário controle especial: ${response.status}`
        );
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Ocorreu um erro ao salvar o receituário");
      setIsSubmitting(false); // REABILITA o botão em caso de erro
    }
  };

  return (
    <div>
      <button
        onClick={() => {
          setIsModalOpen(true);
          fetchReceituariosExistentes(); // Buscar receituários ao abrir o modal
        }}
        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded flex items-center gap-2"
      >
        <FaFileMedicalAlt />
        Receituário Controle Especial
      </button>
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50"
          onClick={handleClose}
        >
          <div
            className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] shadow-lg overflow-y-auto"
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Cabeçalho do Modal */}
            <div className="flex justify-between items-center border-b p-4 sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold">
                Receituário Controle Especial
              </h2>
              <button
                onClick={handleClose}
                aria-label="Fechar modal"
                className="text-gray-700 hover:text-gray-900 transition"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="flex flex-1 overflow-hidden">
              {/* Lista de Receituários Especiais Existentes */}
              {showReceituariosList && (
                <div className="w-80 border-r bg-gray-50 overflow-y-auto">
                  <div className="p-4 border-b">
                    <h3 className="font-bold text-gray-700 mb-2">
                      Receituários Especiais Anteriores
                    </h3>
                    <button
                      onClick={() => setShowReceituariosList(false)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      ← Voltar ao editor
                    </button>
                  </div>

                  {loadingReceituarios ? (
                    <div className="p-4 text-center">
                      <p className="text-gray-500">
                        Carregando receituários...
                      </p>
                    </div>
                  ) : receituariosExistentes.length > 0 ? (
                    <div className="p-2">
                      {receituariosExistentes.map((receituario) => (
                        <div
                          key={receituario.id}
                          className="p-3 mb-2 border rounded-lg bg-white hover:bg-blue-50 cursor-pointer transition-colors"
                          onClick={() =>
                            carregarReceituarioExistente(receituario)
                          }
                        >
                          <div className="font-semibold text-sm text-gray-800">
                            {formatDate(receituario.dtConsulta)}
                          </div>
                          <div className="text-xs text-blue-600 mt-1 font-medium">
                            Dr. {receituario.profissional.nmProfissional}
                          </div>
                          <div className="text-xs text-gray-500 truncate mt-1">
                            {receituario.prescricao
                              .replace(/<[^>]*>/g, "")
                              .substring(0, 60)}
                            ...
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center">
                      <p className="text-gray-500">
                        Nenhum receituário especial anterior encontrado
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Conteúdo Principal */}
              <div
                className={`flex-1 overflow-y-auto ${
                  showReceituariosList ? "" : "w-full"
                }`}
              >
                <div className="p-8 text-sm text-black bg-white">
                  {/* Cabeçalho com Logo e Botão Reutilizar */}
                  <div className="flex justify-between items-start mb-6">
                    {/* Logo + Identificação das Vias */}
                    <div className="relative flex-1">
                      <img
                        src={Logo}
                        alt="Logo Afrafep"
                        className="w-full max-w-xs h-auto object-contain"
                        hidden
                      />
                      <div className="mt-2 flex flex-col text-left text-xs leading-tight text-black">
                        <p className="mb-1">
                          1ª Via - Retenção da Farmácia ou Drogaria
                        </p>
                        <p>2ª Via - Orientação ao Paciente</p>
                      </div>
                    </div>

                    {/* Botão Reutilizar Receituário - só aparece quando NÃO está mostrando a lista */}
                    {!showReceituariosList &&
                      receituariosExistentes.length > 0 && (
                        <div className="flex-shrink-0 ml-4">
                          <button
                            onClick={() => setShowReceituariosList(true)}
                            className="bg-green-600 hover:bg-green-700 text-white py-2 px-3 rounded flex items-center gap-2 text-sm"
                          >
                            <FaRecycle />
                            Reutilizar Receituário
                          </button>
                        </div>
                      )}
                  </div>

                  {/* Identificação do Emitente */}
                  <div className="border border-gray-400 p-4 mb-6 rounded">
                    <p className="font-bold text-center underline mb-3">
                      IDENTIFICAÇÃO DO EMITENTE
                    </p>
                    <div className="text-[12px]">
                      <p>
                        <strong>Nome Completo:</strong> Dr.{" "}
                        {professional.nmProfissional}
                      </p>
                      <p>
                        <strong>CNPJ:</strong> 09.306.242/0001-82
                      </p>
                      <p>
                        <strong>Endereço completo e Telefone:</strong> Rua
                        Corálio Soares de Oliveira, 497 - Centro (83) 3533.5310
                      </p>
                      <p>
                        <strong>Cidade:</strong> João Pessoa
                      </p>
                      <p>
                        <strong>UF:</strong> Paraíba
                      </p>
                    </div>
                  </div>

                  {/* Resto do conteúdo permanece igual */}
                  {/* Informações do Paciente + Prescrição */}
                  <div className="mb-6">
                    <p className="mb-2">
                      <strong>Paciente:</strong> {professional.nomePaciente}
                    </p>
                    <p className="mb-4">
                      <strong>Endereço:</strong> {enderecoPaciente}
                    </p>
                    <p className="font-bold mb-2">Prescrição:</p>
                    <div className="border border-gray-300 rounded">
                      <ReactQuill
                        value={content}
                        onChange={setContent}
                        modules={modules}
                        theme="snow"
                      />
                    </div>
                  </div>

                  <div className="mt-4 p-4 border border-gray-300 rounded bg-gray-50">
                    <h3 className="font-bold text-blue-700 mb-2">
                      Visualização do PDF:
                    </h3>
                    <div className="text-sm text-gray-700 whitespace-pre-wrap bg-white p-3 rounded border">
                      <div className="mb-4">
                        <div className="font-bold text-center underline mb-2">
                          PRESCRIÇÃO MÉDICA
                        </div>
                        <div className="text-xs whitespace-pre-wrap">
                          {obterTextoPreview()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Botões de Ação */}
                  <div className="flex justify-end gap-4 mt-8">
                    <button
                      onClick={() =>
                        setShowReceituariosList(!showReceituariosList)
                      }
                      className="bg-gray-900 hover:bg-gray-800 text-white py-2 px-4 rounded flex items-center gap-2"
                    >
                      <FaRecycle />
                      {showReceituariosList
                        ? "Voltar ao Editor"
                        : "Ver Receituários Anteriores"}
                    </button>

                    <button
                      onClick={handleSaveReceituario}
                      disabled={isSubmitting}
                      className={`${
                        isSubmitting
                          ? "bg-blue-300 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700"
                      } text-white py-2 px-4 rounded flex items-center gap-2`}
                    >
                      <FaSave />
                      {isSubmitting ? "Salvando..." : "Salvar e Gerar PDF"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceituarioControleEspecial;
